import dayjs from 'dayjs'

import type { AmendmentSearchAPIData, TravellerDetailsResponse } from '@/types/amendTraveller'
import { getSourceType } from '@/utils/amend/getAmendSourceType'
import {
  travellerDetailsHasPromotionalOffer,
} from '@/utils/amend/travellerDetailsDerived'

const DD_MM_YYYY = 'DD/MM/YYYY'
const MM_DD_YYYY = 'MM/DD/YYYY'

function parseSectorEndpoint(codeCity: string): { code: string; full: string } {
  const index = codeCity.indexOf('_')
  if (index === -1) return { code: codeCity, full: codeCity }
  return { code: codeCity.slice(0, index), full: codeCity.slice(index + 1) }
}

function formatDate(value: string | Date | null | undefined, pattern: string): string {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format(pattern) : ''
}

function formatConfigAmendDate(value: string | null | undefined): string {
  if (!value) return ''
  const parsed = dayjs(value)
  if (parsed.isValid()) return parsed.format(DD_MM_YYYY)
  return value
}

function getAmendStorageDomain(): string {
  if (typeof window === 'undefined') return ''
  let domain = window.location.hostname
  if (domain.indexOf('0.0.0.0') > -1 || domain.indexOf('localhost') > -1) {
    domain = 'qa2.cleartrip.com'
  }
  return domain
}

export function getAmendStorageSourceType(): string {
  return getSourceType() === 'hq' ? 'hq' : 'ct'
}

export function processTravellerDetailsRequestData(
  tripId: string,
  data: TravellerDetailsResponse
): AmendmentSearchAPIData {
  const flights = data?.flightList ?? []
  const paxList = data?.paxInfoList ?? []
  const onward = flights[0]
  const returnLeg = data?.isRt && flights.length > 1 ? flights[1] : null
  const fromParsed = parseSectorEndpoint(onward?.from ?? '')
  const toParsed = parseSectorEndpoint(onward?.to ?? '')
  const onwardConfig = data.configDaysMap?.[String(onward?.id)]
  const returnConfig = returnLeg ? data.configDaysMap?.[String(returnLeg.id)] : undefined

  return {
    flightIds: flights.map(flight => Number(flight.id)).filter(id => !Number.isNaN(id)),
    paxIds: paxList.map(pax => Number(pax.id)).filter(id => !Number.isNaN(id)),
    paxList: [...paxList],
    isRt: Boolean(data?.isRt),
    from: fromParsed.code,
    from_full: fromParsed.full,
    to: toParsed.code,
    to_full: toParsed.full,
    depart_date: formatDate(onward?.travelDate, DD_MM_YYYY),
    return_date: returnLeg ? formatDate(returnLeg.travelDate, DD_MM_YYYY) : '',
    amend_from_date_depart: formatConfigAmendDate(onwardConfig?.amendFrom),
    amend_to_date_depart: formatConfigAmendDate(onwardConfig?.amendTo),
    amend_from_date_return: formatConfigAmendDate(returnConfig?.amendFrom),
    amend_to_date_return: formatConfigAmendDate(returnConfig?.amendTo),
    depart_date_formatted: formatDate(onward?.travelDate, MM_DD_YYYY),
    return_date_formatted: returnLeg ? formatDate(returnLeg.travelDate, MM_DD_YYYY) : '',
    intl: Boolean(data?.isIntl),
    vasDetails: data?.vasDetails ?? [],
    domain: getAmendStorageDomain(),
    sourceType: getAmendStorageSourceType(),
    tripId,
    isFullyAmmendable: Boolean(data?.isFullyAmmendable),
    tripCountryCode: data?.tripCountryCode ?? '',
    fareDetails: data?.fareDetails ?? [],
    isFlexiFlyTrip: Boolean(data?.isFlexiFlyTrip),
    configDaysMap: data?.configDaysMap ?? {},
    isOfferAvailable: travellerDetailsHasPromotionalOffer({
      flightList: flights,
      isRt: data?.isRt,
      configDaysMap: data?.configDaysMap,
    }),
    ctUpgrade: Boolean(data?.ct_upgrade),
    cabinClass: data?.cabinClass ?? '',
    sft: data?.sft ?? '',
    isNewFlowEnabled: Boolean(data?.isNewFlowEnabled),
  }
}
